import { useMutation } from '@apollo/client';
import type { UpdateUserRolesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateUserRolesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateUserRolesTRPCInput } from './useUpdateUserRolesTRPC';
import { useUpdateUserRolesTRPC } from './useUpdateUserRolesTRPC';

/**
 * Flag-gated wrapper. Consumers ALWAYS import this hook (never the
 * `*TRPC` sibling directly). When the `trpc` feature flag is on, the
 * call is routed to the tRPC mutation; otherwise it falls back to the
 * legacy Apollo `UpdateUserRoles` GraphQL action so deployments without
 * the flag continue to function.
 */
export const useUpdateUserRoles = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // Apollo mutation — legacy fallback. Evict `auth_user` / `user_role`
  // cached entries and refetch the by-id query so the users settings table
  // and detail modal reflect the change without a page reload.
  const [updateUserRolesGraphQL, graphqlState] = useMutation(
    UpdateUserRolesDocument,
    {
      update: (cache) => {
        // user_role/role_type aren't exposed as Hasura root query fields,
        // so they aren't in CacheFieldName — evict the user envelopes
        // (which carry the userRoles relation in their queries) and let
        // refetchQueries below refresh the by-id view.
        evictField(cache, 'auth_user');
        evictField(cache, 'auth_user_by_pk');
      },
      refetchQueries: [namedOperations.Query.GetAuthUserByIdWithRoles],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateUserRolesTRPC();

  const updateUserRoles = async (
    variables: UpdateUserRolesTRPCInput
  ): Promise<UpdateUserRolesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateUserRoles(variables);
    }

    const result = await updateUserRolesGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update user roles');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateUserRoles,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateUserRoles,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
